import {
  absoluteUrl,
  embedPathOf,
  stripHtml,
} from "@/shared/components/MetaHead/metaTags";
import { toIsoDateTime, toIsoDuration } from "@/shared/utils/seoDates";
import { compact, type JsonLdNode } from "./serialize";

/*
 * Сборщики узлов JSON-LD (schema.org) для страниц витрины. Все чистые: на
 * входе host зрителя и данные запросов страницы, на выходе узел графа или
 * null, когда без хоста или без ключевых данных узел собрать нечестно.
 * Ничего не выдумываем: поле без данных просто не пишется (compact).
 * Какие типы на каких страницах: docs/features/seo-meta.md.
 */

/** Платформа в объёме, нужном разметке: имя, лого, соцсети футера */
export interface StructuredPlatform {
  title: string;
  description?: string | null;
  logo?: { url: unknown } | null;
  footer?: { social: { type: string; link: unknown }[] } | null;
}

/** Цена, видимая зрителю на странице: значение, валюта, срок в днях */
export interface StructuredPrice {
  value: number;
  currency: string;
  period?: number | null;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export const ORGANIZATION_FRAGMENT = "#organization";
export const WEBSITE_FRAGMENT = "#website";

const origin = (host: string) => (host ? `https://${host}` : null);

export const organizationId = (host: string) =>
  host ? `${origin(host)}/${ORGANIZATION_FRAGMENT}` : null;

export const websiteId = (host: string) =>
  host ? `${origin(host)}/${WEBSITE_FRAGMENT}` : null;

const text = (value: string | null | undefined) =>
  value ? stripHtml(value) || null : null;

const imageObject = (url: unknown) =>
  url ? { "@type": "ImageObject", url: String(url) } : null;

/** Издатель для publisher/author/organizer: имя и лого платформы */
const publisherOf = (host: string, platform: StructuredPlatform) =>
  compact({
    "@type": "Organization",
    "@id": organizationId(host),
    name: platform.title,
    url: origin(host) ? `${origin(host)}/` : null,
    logo: imageObject(platform.logo?.url),
  });

/**
 * Organization главной: имя, лого и sameAs из соцсетей футера. С @id, чтобы
 * остальные узлы (WebSite.publisher, VideoObject.publisher) ссылались на
 * него, а не дублировали.
 */
export function buildOrganization(
  host: string,
  platform: StructuredPlatform,
): JsonLdNode | null {
  if (!host) return null;
  return compact({
    "@type": "Organization",
    "@id": organizationId(host),
    name: platform.title,
    url: `${origin(host)}/`,
    logo: imageObject(platform.logo?.url),
    sameAs: (platform.footer?.social ?? [])
      .map((social) => String(social.link ?? ""))
      .filter(Boolean),
  });
}

/**
 * WebSite главной с SearchAction: у витрины есть /search, страница читает
 * запрос из ?query= (SearchPage.tsx), поэтому шаблон адреса именно такой.
 */
export function buildWebSite(
  host: string,
  platform: StructuredPlatform,
): JsonLdNode | null {
  if (!host) return null;
  return compact({
    "@type": "WebSite",
    "@id": websiteId(host),
    url: `${origin(host)}/`,
    name: platform.title,
    description: text(platform.description),
    publisher: { "@id": organizationId(host) },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${origin(host)}/search?query={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  });
}

/**
 * Хлебные крошки: главная (имя платформы), затем переданные элементы
 * (коллекция, если известна, и сама страница).
 */
export function buildBreadcrumbs(
  host: string,
  platform: StructuredPlatform,
  items: (BreadcrumbItem | null | undefined)[],
): JsonLdNode | null {
  if (!host) return null;
  const crumbs: BreadcrumbItem[] = [
    { name: platform.title, path: "/" },
    ...items.filter((item): item is BreadcrumbItem => Boolean(item)),
  ];
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(host, crumb.path),
    })),
  };
}

/** Предложение (цена страницы): PPV и подписка одинаково, срок в описании */
export function buildOffer(
  host: string,
  path: string,
  price: StructuredPrice,
  validFrom?: unknown,
): JsonLdNode {
  return compact({
    "@type": "Offer",
    url: absoluteUrl(host, path),
    price: price.value,
    priceCurrency: price.currency,
    availability: "https://schema.org/InStock",
    validFrom: toIsoDateTime(validFrom),
  });
}

export interface StructuredStream {
  status: string;
  startAt?: unknown;
  finishAt?: unknown;
}

export interface StructuredContent {
  id: string;
  title: string;
  description?: string | null;
  path: string;
  publishAt?: unknown;
  duration?: number | null;
  premium?: boolean | null;
  tags?: { title: string }[] | null;
  stream?: StructuredStream | null;
}

export const videoObjectId = (host: string, path: string) =>
  absoluteUrl(host, path) ? `${absoluteUrl(host, path)}#video` : null;

export const eventId = (host: string, path: string) =>
  absoluteUrl(host, path) ? `${absoluteUrl(host, path)}#event` : null;

/**
 * VideoObject видео и стримов. У платного контента isAccessibleForFree:
 * false и разметка пейволла (hasPart WebPageElement с селектором .paywall,
 * класс стоит на корневой обёртке ContentPlayer платного контента и есть
 * уже в SSR-HTML, до проверки доступа). У стрима вместо
 * длительности publication BroadcastEvent: isLiveBroadcast, startDate это
 * плановое или фактическое начало (stream.startAt), endDate у
 * завершённого; broadcastOfEvent ссылается на Event-узел той же страницы.
 */
export function buildVideoObject(
  host: string,
  platform: StructuredPlatform,
  content: StructuredContent,
  thumbnailUrl: string | null,
  options: { linkedEventId?: string | null } = {},
): JsonLdNode | null {
  const url = absoluteUrl(host, content.path);
  if (!url) return null;

  const stream = content.stream ?? null;
  const publication = stream
    ? compact({
        "@type": "BroadcastEvent",
        isLiveBroadcast: true,
        startDate: toIsoDateTime(stream.startAt),
        endDate:
          stream.status === "FINISHED" ? toIsoDateTime(stream.finishAt) : null,
        broadcastOfEvent: options.linkedEventId
          ? { "@id": options.linkedEventId }
          : null,
      })
    : null;

  return compact({
    "@type": "VideoObject",
    "@id": videoObjectId(host, content.path),
    name: content.title,
    description: text(content.description) ?? content.title,
    url,
    mainEntityOfPage: url,
    thumbnailUrl: thumbnailUrl || null,
    uploadDate: toIsoDateTime(content.publishAt),
    duration: stream ? null : toIsoDuration(content.duration),
    embedUrl: absoluteUrl(host, embedPathOf(content.id)),
    keywords: (content.tags ?? []).map((tag) => tag.title).filter(Boolean),
    publisher: publisherOf(host, platform),
    isAccessibleForFree: content.premium ? false : null,
    hasPart: content.premium
      ? {
          "@type": "WebPageElement",
          isAccessibleForFree: false,
          cssSelector: ".paywall",
        }
      : null,
    publication,
  });
}

export interface StructuredEventInput {
  /** Путь страницы события: стрим или продукт */
  path: string;
  name: string;
  description?: string | null;
  image?: string | null;
  startDate: unknown;
  endDate?: unknown;
  /** Видимая цена; без неё событие помечается бесплатным */
  price?: StructuredPrice | null;
  /** validFrom предложения: publishAt контента или продукта */
  validFrom?: unknown;
}

/**
 * Event для расширенных результатов Google (онлайн-событие): стрим в
 * ожидании или в эфире и PPV-продукт с датой события. Статус всегда
 * EventScheduled: состояния «отменён» ни у стрима, ни у продукта нет,
 * завершённый стрим Event не получает вовсе (остаётся VideoObject).
 */
export function buildEvent(
  host: string,
  platform: StructuredPlatform,
  input: StructuredEventInput,
): JsonLdNode | null {
  const url = absoluteUrl(host, input.path);
  const startDate = toIsoDateTime(input.startDate);
  if (!url || !startDate) return null;

  return compact({
    "@type": "Event",
    "@id": eventId(host, input.path),
    name: input.name,
    description: text(input.description) ?? input.name,
    image: input.image || null,
    url,
    startDate,
    endDate: toIsoDateTime(input.endDate),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    location: { "@type": "VirtualLocation", url },
    organizer: publisherOf(host, platform),
    isAccessibleForFree: input.price ? null : true,
    offers: input.price
      ? buildOffer(host, input.path, input.price, input.validFrom)
      : null,
  });
}

export interface StructuredProduct {
  id: string;
  title: string;
  description?: string | null;
  path: string;
}

/**
 * Product с Offer по видимой цене. У подписки срок дописывается к описанию
 * (periodLabel готовит экран переводом), у PPV описание как есть. Без
 * видимой цены остаётся Product без offers.
 */
export function buildProduct(
  host: string,
  product: StructuredProduct,
  imageUrl: string | null,
  price: StructuredPrice | null,
  options: { periodLabel?: string | null; validFrom?: unknown } = {},
): JsonLdNode | null {
  const url = absoluteUrl(host, product.path);
  if (!url) return null;

  const description = text(product.description) ?? product.title;
  return compact({
    "@type": "Product",
    "@id": `${url}#product`,
    name: product.title,
    description: options.periodLabel
      ? `${description} (${options.periodLabel})`
      : description,
    image: imageUrl || null,
    url,
    offers: price
      ? buildOffer(host, product.path, price, options.validFrom)
      : null,
  });
}

/**
 * Страница коллекции: CollectionPage плюс ItemList видимых детей (первая
 * страница видео коллекции в её порядке) с позицией, адресом и названием.
 */
export function buildCollection(
  host: string,
  content: StructuredContent,
  imageUrl: string | null,
  items: { title: string; path: string }[],
): JsonLdNode[] {
  const url = absoluteUrl(host, content.path);
  if (!url) return [];

  const listId = `${url}#items`;
  const list = compact({
    "@type": "ItemList",
    "@id": listId,
    name: content.title,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.title,
      url: absoluteUrl(host, item.path),
    })),
  });
  const page = compact({
    "@type": "CollectionPage",
    "@id": `${url}#page`,
    url,
    name: content.title,
    description: text(content.description) ?? content.title,
    image: imageUrl || null,
    keywords: (content.tags ?? []).map((tag) => tag.title).filter(Boolean),
    mainEntity: { "@id": listId },
  });
  return [page, list];
}

export interface StructuredPost {
  title: string;
  description?: string | null;
  path: string;
  publishAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  tags?: { title: string }[] | null;
}

/** Article поста: автор и издатель это организация платформы */
export function buildArticle(
  host: string,
  platform: StructuredPlatform,
  post: StructuredPost,
  imageUrl: string | null,
): JsonLdNode | null {
  const url = absoluteUrl(host, post.path);
  if (!url) return null;

  const publisher = publisherOf(host, platform);
  return compact({
    "@type": "Article",
    "@id": `${url}#article`,
    headline: post.title,
    description: text(post.description),
    image: imageUrl || null,
    url,
    mainEntityOfPage: url,
    datePublished: toIsoDateTime(post.publishAt ?? post.createdAt),
    dateModified: toIsoDateTime(post.updatedAt),
    keywords: (post.tags ?? []).map((tag) => tag.title).filter(Boolean),
    author: publisher,
    publisher,
  });
}
